/* hs-eslint ignored failing-rules */

/* eslint-disable no-use-before-define */

/* eslint-disable no-bitwise */
'use es6';

import PropTypes from 'prop-types';
import ReactUtilsPropTypes from 'react-utils/CustomPropTypes';
import Observable from 'react-codemirror/model/Observable';
import { omit } from '../util/miniUnderscore.js';
const privy = new WeakMap();

const stringValidator = val => {
  return val && typeof val === 'string';
};

const uintValidator = val => {
  return val === val >>> 0;
};

const boolValidator = val => {
  return !!val === val;
};

const setComponentProp = (props, field, value) => {
  if (value === undefined) {
    if (field in props) {
      return omit(props, field);
    }

    return props;
  }

  switch (field) {
    case 'fontSize':
      props[field] = `${value}px`;
      break;

    default:
      props[field] = value;
      break;
  }

  return props;
};

const createComponentProps = model => {
  let props = {};
  Settings.DEFAULT_FIELDS.forEach(({
    name
  }) => {
    props = setComponentProp(props, name, model[name]);
  });
  return props;
};

const updateComponentPropsOnChange = ({
  field,
  value,
  model
}) => {
  const {
    componentProps
  } = privy.get(model);
  let props = Object.assign({}, componentProps);
  props = setComponentProp(props, field, value);
  privy.get(model).componentProps = Object.freeze(props);
};
/**
 * @typedef {Observable} Settings
 * @property {Boolean}  autoCloseBrackets
 * @property {Boolean}  autoCloseTags
 * @property {Boolean}  continueComments
 * @property {Boolean}  disableSoftTabs
 * @property {Boolean}  electricChars
 * @property {String}   fontFamily
 * @property {UInt32}   fontSize
 * @property {UInt32}   indentUnit
 * @property {Boolean}  indentWithTabs
 * @property {Boolean}  lineWrapping
 * @property {Boolean}  matchBrackets
 * @property {Boolean}  matchTags
 * @property {Boolean}  smartIndent
 * @property {UInt}     tabSize
 * @property {String}   theme
 */

/**
 * @classdesc CodeMirror configurable settings.
 * @class
 * @extends Observable
 */


export default class Settings extends Observable {
  constructor(fields) {
    super(fields);
    privy.set(this, {
      componentProps: Object.freeze(createComponentProps(this))
    });
    this.on(updateComponentPropsOnChange);
  }

  get componentProps() {
    return privy.get(this).componentProps;
  }

}
Settings.DEFAULT_FIELDS = Object.freeze([{
  name: 'autoCloseBrackets',
  value: false,
  validator: boolValidator
}, {
  name: 'autoCloseTags',
  value: true,
  validator: boolValidator
}, {
  name: 'continueComments',
  value: true,
  validator: boolValidator
}, {
  name: 'disableSoftTabs',
  value: false,
  validator: boolValidator
}, {
  name: 'electricChars',
  value: true,
  validator: boolValidator
}, {
  name: 'fontFamily',
  value: 'Source Code Pro',
  validator: stringValidator
}, {
  name: 'fontSize',
  value: 12,
  validator: uintValidator
}, {
  name: 'indentUnit',
  value: 2,
  validator: uintValidator
}, {
  name: 'indentWithTabs',
  value: false,
  validator: boolValidator
}, {
  name: 'lineWrapping',
  value: false,
  validator: boolValidator
}, {
  name: 'matchBrackets',
  value: true,
  validator: boolValidator
}, {
  name: 'matchTags',
  value: true,
  validator: boolValidator
}, // 'rulers',
// { name: 'showTrailingSpace', value: false, validator: boolValidator, },
{
  name: 'smartIndent',
  value: true,
  validator: boolValidator
}, {
  name: 'tabSize',
  value: 2,
  validator: uintValidator
}, {
  name: 'theme',
  value: 'hubspot-canvas-dark',
  validator: stringValidator
}]);
/**
 * @type {Function}
 */

Settings.shapePropType = PropTypes.shape({
  autoCloseBrackets: PropTypes.bool,
  autoCloseTags: PropTypes.bool,
  continueComments: PropTypes.bool,
  disableSoftTabs: PropTypes.bool,
  electricChars: PropTypes.bool,
  fontFamily: PropTypes.string,
  fontSize: ReactUtilsPropTypes.uint32,
  indentUnit: ReactUtilsPropTypes.uint32,
  indentWithTabs: PropTypes.bool,
  lineWrapping: PropTypes.bool,
  matchBrackets: PropTypes.bool,
  matchTags: PropTypes.bool,
  // rulers: PropTypes.object,
  // showTrailingSpace: PropTypes.bool,
  smartIndent: PropTypes.bool,
  tabSize: ReactUtilsPropTypes.uint32,
  theme: PropTypes.string
});
/**
 * Creates or gets a Settings instance by userId.
 *
 * @param {Number} userId
 */

Settings.getByUserId = function getByUserId(userId) {
  const {
    cache
  } = Settings.getByUserId;

  if (cache[userId]) {
    return cache[userId];
  }

  return cache[userId] = new Settings();
};

Settings.getByUserId.cache = {};